import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValue } from "@tellescope/types-models";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { expandSubmittedFieldIds } from "../../utils";
import type { FormFieldNode } from "../../utils/types";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";
import { buildResponsesToSubmit } from "./build-responses-to-submit";

export interface UseEnduserSubmitFormArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
  activeField: FormFieldNode | undefined;
  includedFieldIds: Set<string>;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  existingResponses: FormResponseValue[] | undefined;
  response: { accessCode?: string } | undefined;
  formId: string;
  enduserId: string;
  parentResponseId: string | undefined;
  rootResponseId: string | undefined;
}

export const useEnduserSubmitForm = ({
  core,
  activeField,
  includedFieldIds,
  fieldsMap,
  existingResponses,
  response,
  formId,
  enduserId,
  parentResponseId,
  rootResponseId,
}: UseEnduserSubmitFormArgs) => {
  const { t } = useTranslation("forms");

  return useCallback(async () => {
    const currentError = await core.validateField({ fieldId: activeField?.value.id ?? "" });
    if (currentError) {
      throw new Error(t("Please fix validation errors before submitting"));
    }

    return core.runSubmit(async (uploadFirst) => {
      let formResponse = response ?? core.preparedResponse;

      if (!formResponse) {
        const res = await core.prepareFormResponseMutation.mutateAsync({
          formId,
          enduserId,
          parentResponseId,
          rootResponseId,
          title: core.formTitle,
        });
        formResponse = res.response;
      }

      if (!formResponse || !formResponse.accessCode) {
        throw new Error(t("Failed to prepare form response"));
      }

      const submittedFieldIds = expandSubmittedFieldIds(includedFieldIds, fieldsMap, core.isSubFieldHidden);

      const responsesAfterUpload = await uploadFirst();

      const responsesToSubmit = buildResponsesToSubmit({
        responsesAfterUpload,
        existingResponses,
        submittedFieldIds,
      });

      const productIds = responsesToSubmit.flatMap((r) => core.fields?.fieldsMap[r.fieldId]?.options?.productIds ?? []);

      const result = await core.submitFormMutation.mutateAsync({
        accessCode: formResponse.accessCode,
        responses: responsesToSubmit,
        markedAsSubmitted: true,
        ...(core.customerId ? { customerId: core.customerId } : {}),
        ...(productIds.length ? { productIds } : {}),
      });

      return result;
    });
  }, [
    core,
    response,
    activeField?.value.id,
    formId,
    enduserId,
    parentResponseId,
    rootResponseId,
    includedFieldIds,
    existingResponses,
    fieldsMap,
    t,
  ]);
};
